/*
 * Entry point of the application.
 * Only platform bootstrapping code should be here.
 * For app-specific initialization, use `app/app.component.ts`.
 */

import { APP_INITIALIZER, enableProdMode, importProvidersFrom } from '@angular/core';
// import { AppRoutingModule, routes } from '@app/app-routing.module';
import { environment } from '@env/environment';
import * as LogRocket from 'logrocket';
import { register as registerSwiperElements } from 'swiper/element/bundle';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { FormsModule } from '@angular/forms';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from '@app/app-routing.module';
import { AppComponent } from '@app/app.component';
import { ScreensModule } from '@app/screens/screens.module';
import { ShellModule } from '@app/shell/shell.module';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AuthEffects, SharedModule } from '@shared';
import { GeolocationAttachmentInterceptor } from '@shared/interceptors/geolocation-attachment.interceptor';
import { AuthenticationService } from '@shared/services/auth/authentication.service';
import {
  ApiPrefixInterceptor,
  CourseDetailsV2Effects,
  courseDetailsV2Feature,
  CourseEffects,
  courseFeature,
  ErrorHandlerInterceptor,
  LoadingInterceptor,
  RouteReusableStrategy,
  SessionRefreshInterceptor,
  SPINNER_ICON_SRC,
} from 'thkee-common';
import { initSuperTokensWebJS } from './config-token';

// Init provider for super token custom header
const appInitializerProvider = {
  provide: APP_INITIALIZER,
  useFactory: (authService: AuthenticationService) => {
    return async () => {
      // await authService.initGeolocation();
      initSuperTokensWebJS(() => authService.getCountryHeader());
    };
  },
  deps: [AuthenticationService],
  multi: true,
};

if (environment.production) {
  enableProdMode();
}

if (environment.production && environment.logRocketEnabled) {
  LogRocket.init('jfz8x8/thkee-platform'); // Replace with your LogRocket ID

  // LogRocket.getSessionURL((sessionURL) => {
  //   if (window['gtag']) {
  //     window['gtag']('event', 'logrocket_session', {
  //       event_category: 'LogRocket',
  //       event_label: sessionURL,
  //     });
  //   }
  // });
}

registerSwiperElements();
// platformBrowserDynamic()
//   .bootstrapModule(AppModule)
//   .catch((err) => console.error(err));

const httpProvider = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ApiPrefixInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: SessionRefreshInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorHandlerInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: LoadingInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: GeolocationAttachmentInterceptor,
    multi: true,
  },
  {
    provide: RouteReuseStrategy,
    useClass: RouteReusableStrategy,
  },
  {
    provide: SPINNER_ICON_SRC,
    useValue: '@assets/images/icons/spinner.svg',
  },
];

const providerModules = importProvidersFrom(
  HttpClientModule,
  AngularFireAuthModule,
  AngularFireModule.initializeApp(environment.firebaseConfig),
  BrowserModule,
  ServiceWorkerModule.register('./ngsw-worker.js', { enabled: environment.production }),
  FormsModule,
  HttpClientModule,

  StoreModule.forRoot(
    {},
    {
      runtimeChecks: {
        strictStateImmutability: true,
        strictStateSerializability: true,
        strictActionImmutability: true,
        strictActionSerializability: true,
        strictActionTypeUniqueness: true,
        strictActionWithinNgZone: true,
      },
    }
  ),
  StoreDevtoolsModule.instrument({}),
  EffectsModule.forRoot([AuthEffects]),
  EffectsModule.forFeature([CourseEffects]),
  StoreModule.forFeature(courseFeature),
  EffectsModule.forFeature([CourseDetailsV2Effects]),
  StoreModule.forFeature(courseDetailsV2Feature),
  StoreRouterConnectingModule.forRoot(),
  SharedModule,
  ShellModule,
  ScreensModule,
  AppRoutingModule // must be imported as the last module as it contains the fallback route
);

bootstrapApplication(AppComponent, {
  providers: [providerModules, ...httpProvider, appInitializerProvider],
});
